import { Link } from "react-router-dom";
import type { Story } from "@/mocks/stories";
import type { Collection } from "@/mocks/collections";

/* ---------- Section heading with kicker ---------- */
export function SectionHead({
  kicker,
  title,
  linkTo,
  linkLabel,
}: {
  kicker: string;
  title: string;
  linkTo?: string;
  linkLabel?: string;
}) {
  return (
    <div className="mb-6 flex items-end justify-between gap-4">
      <div>
        <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-primary-600">
          <span className="h-2.5 w-2.5 rounded-sm bg-primary-500" />
          {kicker}
        </p>
        <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          {title}
        </h2>
      </div>
      {linkTo && linkLabel ? (
        <Link
          to={linkTo}
          className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-sm font-bold text-foreground-600 transition-colors hover:text-primary-600"
        >
          {linkLabel}
          <i className="ri-arrow-right-line" />
        </Link>
      ) : null}
    </div>
  );
}

/* ---------- Vertical story card ---------- */
export function StoryCardVertical({ story }: { story: Story }) {
  return (
    <article className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
      <Link to={`/story/${story.slug}`} className="block">
        <div className="relative aspect-[16/10] w-full overflow-hidden bg-background-200">
          <img
            src={story.cover}
            alt={story.coverAlt}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <span className="absolute left-3 top-3 rounded-full bg-background-950/80 px-2.5 py-1 text-[11px] font-bold text-background-50 backdrop-blur">
            {story.tag}
          </span>
        </div>
      </Link>
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <Link
          to={`/story/${story.slug}`}
          className="font-heading text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700 md:text-lg"
        >
          {story.title}
        </Link>
        <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">
          {story.lead}
        </p>
        <div className="mt-4 flex items-center justify-between gap-2 border-t border-background-200 pt-3 text-xs text-foreground-500">
          <span className="flex items-center gap-1.5">
            <i className="ri-quill-pen-line" />
            {story.author}
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-fire-fill text-primary-500" />
            {story.heat}
          </span>
        </div>
      </div>
    </article>
  );
}

/* ---------- Horizontal feed row ---------- */
export function StoryRow({ story, index }: { story: Story; index?: number }) {
  return (
    <article className="group flex items-start gap-3 rounded-lg border border-transparent p-2 transition-colors hover:border-background-200 hover:bg-background-100/60 md:gap-4">
      {typeof index === "number" ? (
        <span className="font-heading text-2xl font-black leading-none text-primary-500/80 md:text-3xl">
          {String(index).padStart(2, "0")}
        </span>
      ) : null}
      <div className="relative hidden h-20 w-28 shrink-0 overflow-hidden rounded-md bg-background-200 sm:block md:h-24 md:w-36">
        <img
          src={story.cover}
          alt={story.coverAlt}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="min-w-0 flex-1">
        <div className="mb-1 flex items-center gap-2 text-[11px] font-bold text-foreground-500">
          <span className="rounded-full bg-secondary-200 px-2 py-0.5 text-secondary-900">
            {story.tag}
          </span>
          <span>{story.date}</span>
        </div>
        <h3 className="line-clamp-2 text-sm font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700 md:text-base">
          <Link to={`/story/${story.slug}`}>{story.title}</Link>
        </h3>
        <p className="mt-1.5 hidden text-xs text-foreground-600 md:line-clamp-2">
          {story.lead}
        </p>
      </div>
      <span className="hidden shrink-0 items-center gap-1 text-xs font-bold text-primary-600 sm:flex">
        {story.heat}
        <i className="ri-fire-line" />
      </span>
    </article>
  );
}

/* ---------- Big ranking tile ---------- */
export function RankCard({ story, rank }: { story: Story; rank: number }) {
  const top = rank <= 3;
  return (
    <Link
      to={`/story/${story.slug}`}
      className="group flex items-center gap-4 rounded-lg border border-transparent p-2 transition-colors hover:border-background-200 hover:bg-background-50"
    >
      <span
        className={
          "flex h-9 w-9 shrink-0 items-center justify-center rounded-md font-heading text-lg font-black " +
          (top
            ? "bg-primary-500 text-background-50"
            : "bg-background-200 text-foreground-600")
        }
      >
        {rank}
      </span>
      <div className="min-w-0 flex-1">
        <h3 className="line-clamp-2 text-sm font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700">
          {story.title}
        </h3>
        <p className="mt-1 flex items-center gap-3 text-xs text-foreground-500">
          <span>{story.tag}</span>
          <span className="inline-flex items-center gap-1">
            <i className="ri-fire-fill text-primary-500" />
            {story.heat}
          </span>
        </p>
      </div>
    </Link>
  );
}

/* ---------- Collection tile ---------- */
export function CollectionCard({ collection }: { collection: Collection }) {
  return (
    <Link
      to={`/collection/${collection.id}`}
      className="group relative block overflow-hidden rounded-lg border border-background-200 bg-background-50"
    >
      <div className="relative aspect-[16/9] w-full overflow-hidden bg-background-200">
        <img
          src={collection.cover}
          alt={collection.coverAlt}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-950/55 via-transparent to-transparent" />
        <div className="absolute inset-x-0 bottom-0 p-4">
          <div className="flex flex-wrap gap-1.5">
            {collection.topic.map((t) => (
              <span
                key={t}
                className="rounded-full bg-background-50/85 px-2 py-0.5 text-[11px] font-bold text-foreground-800"
              >
                {t}
              </span>
            ))}
          </div>
        </div>
      </div>
      <div className="flex items-start justify-between gap-3 p-4">
        <div>
          <h3 className="font-heading text-lg font-bold text-foreground-950 transition-colors group-hover:text-primary-700">
            {collection.name}
          </h3>
          <p className="mt-0.5 text-xs text-foreground-500">{collection.subtitle}</p>
        </div>
        <span className="mt-1 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-background-300 text-foreground-600 transition-colors group-hover:border-primary-400 group-hover:text-primary-600">
          <i className="ri-arrow-right-up-line" />
        </span>
      </div>
    </Link>
  );
}